"use client";

import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  SITE_BUILDER_ICON_CATALOG,
  type SiteBuilderIconCatalogItem,
  type SiteBuilderIconCategory,
} from "@webdo/site-templates/builder";
import { Modal } from "@/app/components/Modal";

export type BuilderIconLibraryLabels = {
  categoryLabel: (category: SiteBuilderIconCategory) => string;
  close: string;
  empty: string;
  itemLabel: (iconId: string) => string;
  search: string;
  title: string;
};

const iconCategories: readonly SiteBuilderIconCategory[] = [
  "icons",
  "emoji",
  "social",
];

function BuilderIconLibraryGlyph({
  className,
  glyph,
}: {
  className?: string;
  glyph: "search" | "sparkle";
}) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    >
      {glyph === "search" ? (
        <>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m16 16 5 5" />
        </>
      ) : (
        <>
          <path d="m12 3 1.4 4.6L18 9l-4.6 1.4L12 15l-1.4-4.6L6 9l4.6-1.4L12 3Z" />
          <path d="m18.5 15 .7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3Z" />
        </>
      )}
    </svg>
  );
}

function SocialIconLibraryPreview({
  className,
  value,
}: {
  className?: string;
  value: string;
}) {
  const network = value.startsWith("social:")
    ? value.slice("social:".length)
    : "";

  if (network === "email") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="6" width="16" height="12" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="m5 8 7 5 7-5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
      </svg>
    );
  }

  if (network === "facebook") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14.2 8.2V6.9c0-.7.5-1.1 1.2-1.1h1.4V3.4c-.7-.1-1.5-.2-2.3-.2-2.4 0-4.1 1.5-4.1 4.2v.8H7.8v2.9h2.6v9.7h3.2v-9.7h2.6l.4-2.9h-3Z" fill="currentColor" />
      </svg>
    );
  }

  if (network === "instagram") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="17" cy="7" r="1.25" fill="currentColor" />
      </svg>
    );
  }

  if (network === "youtube") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="6" width="18" height="12" rx="4" fill="currentColor" />
        <path d="m10 9 6 3-6 3V9Z" fill="white" />
      </svg>
    );
  }

  if (network === "x") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 4h4.2l3.6 5 4.4-5H20l-5.9 6.8L20 20h-4.2l-4-5.5L7 20H4.2l6.3-7.2L5 4Zm2.7 1.7 9.1 12.6h1.5L9.2 5.7H7.7Z" fill="currentColor" />
      </svg>
    );
  }

  if (network === "linkedin") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7.1 20H3.8V8.9h3.3V20ZM5.5 7.4a1.9 1.9 0 1 1 0-3.8 1.9 1.9 0 0 1 0 3.8ZM20.2 20h-3.3v-5.5c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V20H9.7V8.9h3.1v1.5h.1c.4-.8 1.5-1.7 3.1-1.7 3.3 0 3.9 2.2 3.9 5V20Z" fill="currentColor" />
      </svg>
    );
  }

  if (network === "pinterest") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12.1 3.1c-4.6 0-7.5 3.1-7.5 6.8 0 2.1 1.2 3.8 2.8 4.4.3.1.5 0 .6-.3l.2-.9c.1-.3 0-.5-.2-.8-.6-.7-.9-1.5-.9-2.4 0-2.7 2.1-4.8 5.1-4.8 2.8 0 4.6 1.7 4.6 4.2 0 3-1.4 5.6-3.5 5.6-1.1 0-1.9-.9-1.7-2 .3-1.2.9-2.5.9-3.4 0-.8-.4-1.5-1.3-1.5-1 0-1.8 1.1-1.8 2.5 0 .9.3 1.5.3 1.5l-1.2 5.1c-.3 1.3-.2 3-.1 4.1 0 .2.2.2.3.1.5-.7 1.7-2.5 2.1-4l.5-1.8c.5.8 1.6 1.5 2.9 1.5 3.9 0 6.5-3.6 6.5-8.1 0-3.5-3-6.6-7.6-6.6Z" fill="currentColor" />
      </svg>
    );
  }

  if (network === "whatsapp") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true" fill="none">
        <path d="M4.7 19.4 5.8 16A7.5 7.5 0 1 1 8 18.1l-3.3 1.3Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        <path d="M9 8.4c-.3 0-.7.1-.9.4-.3.4-.8 1-.8 2 0 1.2.9 2.4 1 2.6.1.2 1.8 2.9 4.4 3.9 1.9.8 2.3.6 2.8.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.2-.2-.5-.3l-1.6-.8c-.2-.1-.4-.1-.6.1l-.7.8c-.2.2-.3.2-.6.1-.3-.1-1.1-.4-2.1-1.3-.8-.7-1.3-1.6-1.5-1.9-.2-.3 0-.4.1-.6l.4-.4c.1-.2.2-.3.3-.5.1-.2.1-.3 0-.5l-.7-1.6c-.2-.2-.4-.2-.6-.2H9Z" fill="currentColor" />
      </svg>
    );
  }

  if (network === "tiktok") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M16.7 4c.4 1.6 1.5 2.8 3.3 3v3.1c-1.2 0-2.3-.4-3.3-1v5.7a5.2 5.2 0 1 1-5.2-5.2c.4 0 .8 0 1.1.1V13c-.3-.1-.7-.2-1.1-.2a2 2 0 1 0 2 2V4h3.2Z" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.1.1l2-2A5 5 0 0 0 12 4l-1.1 1.1" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
      <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

function renderIconLibraryPreview(
  item: SiteBuilderIconCatalogItem,
): ReactNode {
  if (item.value.startsWith("social:")) {
    return (
      <SocialIconLibraryPreview
        className="h-5 w-5"
        value={item.value}
      />
    );
  }

  return item.preview;
}

export function BuilderIconLibrary({
  labels,
  onClose,
  onSelect,
  open,
  value,
}: {
  labels: BuilderIconLibraryLabels;
  onClose: () => void;
  onSelect: (value: string) => void;
  open: boolean;
  value: string;
}) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSearch("");
    }
  }, [open]);

  const filteredIcons = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();

    if (query === "") {
      return SITE_BUILDER_ICON_CATALOG;
    }

    return SITE_BUILDER_ICON_CATALOG.filter((item) => (
      labels.itemLabel(item.id).toLocaleLowerCase().includes(query)
      || labels.categoryLabel(item.category).toLocaleLowerCase().includes(query)
      || item.value.toLocaleLowerCase().includes(query)
    ));
  }, [labels, search]);

  const groups = iconCategories
    .map((category) => ({
      category,
      items: filteredIcons.filter((item) => item.category === category),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <Modal
      onClose={onClose}
      open={open}
      size="xl"
    >
      <div
        className="relative flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border/80 bg-bg-elevated/95 shadow-[0_24px_80px_rgba(0,0,0,0.24)] backdrop-blur-xl"
        data-builder-icon-library="true"
        onClick={(event) => event.stopPropagation()}
        style={{ height: "min(700px, calc(100vh - 6rem))" }}
      >
        <div className="relative flex shrink-0 items-center justify-between gap-4 overflow-hidden border-b border-border/80 bg-gradient-to-r from-primary/10 via-bg-elevated to-accent/10 px-5 py-4">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-12 -top-16 h-36 w-36 rounded-full bg-primary/15 blur-3xl"
          />
          <div className="relative flex min-w-0 items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-primary/20 bg-primary/10 text-primary shadow-sm">
              <BuilderIconLibraryGlyph
                className="h-5 w-5"
                glyph="sparkle"
              />
            </span>
            <span className="flex min-w-0 items-center gap-2">
              <h3 className="truncate text-base font-semibold text-text">
                {labels.title}
              </h3>
              <span className="shrink-0 rounded-full border border-border bg-bg-elevated/80 px-2 py-0.5 text-[10px] font-semibold text-text-muted shadow-sm">
                {filteredIcons.length}
              </span>
            </span>
          </div>
          <button
            type="button"
            aria-label={labels.close}
            className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full border border-border bg-bg-elevated/80 text-lg text-text-secondary shadow-sm transition-all hover:border-primary/40 hover:bg-surface-hover hover:text-text"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="shrink-0 space-y-3 border-b border-border/70 bg-bg/60 px-4 py-3.5">
          <div className="relative">
            <BuilderIconLibraryGlyph
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
              glyph="search"
            />
            <input
              aria-label={labels.search}
              className="h-11 w-full rounded-xl border border-border bg-bg-elevated/90 pl-10 pr-4 text-sm text-text shadow-sm outline-none transition-all placeholder:text-text-muted focus:border-primary focus:ring-4 focus:ring-primary/10"
              placeholder={labels.search}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {iconCategories.map((category) => {
              const count = filteredIcons.filter(
                (item) => item.category === category,
              ).length;

              return (
                <span
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold",
                    count > 0
                      ? "border-primary/20 bg-primary/10 text-primary"
                      : "border-border bg-bg-elevated text-text-muted",
                  ].join(" ")}
                  key={category}
                >
                  {labels.categoryLabel(category)}
                  <span className="rounded-full bg-bg-elevated/80 px-1.5 py-0.5 text-[10px] leading-none text-text-secondary shadow-sm">
                    {count}
                  </span>
                </span>
              );
            })}
          </div>
        </div>

        {groups.length > 0 ? (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-bg/40 p-4">
            {groups.map((group) => (
              <section
                className="overflow-hidden rounded-2xl border border-border/70 bg-bg-elevated/70 shadow-sm"
                key={group.category}
              >
                <div className="flex items-center justify-between border-b border-border/60 bg-surface/60 px-4 py-3">
                  <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-secondary">
                    {labels.categoryLabel(group.category)}
                  </h3>
                  <span className="rounded-full border border-border bg-bg px-2 py-0.5 text-[10px] font-semibold text-text-muted">
                    {group.items.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2.5 p-3 sm:grid-cols-3 lg:grid-cols-4">
                  {group.items.map((item) => {
                    const active = value === item.value;

                    return (
                      <button
                        type="button"
                        aria-pressed={active}
                        className={[
                          "group flex min-h-[68px] items-center gap-3 rounded-xl border px-3 py-2.5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md",
                          active
                            ? "border-primary bg-gradient-to-br from-primary/15 via-primary/5 to-accent/10 ring-2 ring-primary/20"
                            : "border-border/80 bg-bg-elevated/90 hover:bg-surface-hover",
                        ].join(" ")}
                        data-builder-icon-library-value={item.value}
                        key={item.value}
                        onClick={() => {
                          onSelect(item.value);
                          onClose();
                        }}
                      >
                        <span
                          className={[
                            "grid h-10 w-10 shrink-0 place-items-center rounded-xl border text-lg font-semibold shadow-sm transition-colors",
                            active
                              ? "border-primary bg-primary text-white"
                              : "border-border bg-bg text-text group-hover:border-primary/30 group-hover:text-primary",
                          ].join(" ")}
                        >
                          {renderIconLibraryPreview(item)}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-text">
                            {labels.itemLabel(item.id)}
                          </span>
                          <span className="mt-0.5 block truncate text-[11px] text-text-muted">
                            {labels.categoryLabel(item.category)}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="m-4 grid min-h-[260px] flex-1 place-items-center rounded-2xl border border-dashed border-border-strong bg-bg/50 px-4 py-8 text-center text-sm text-text-muted">
            {labels.empty}
          </div>
        )}
      </div>
    </Modal>
  );
}
